<template>
  <div class="kt-portlet kt-portlet--mobile">
    <div class="kt-portlet__head kt-portlet__head--lg">
      <div class="kt-portlet__head-label">
        <span class="kt-portlet__head-icon">
          <i class="fas fa-file-alt"></i>
        </span>
        <h3 class="kt-portlet__head-title">
          เอกสารสรุปสินค้าคงค้าง
        </h3>
      </div>
    </div>
    <!--
      QA 08/10/2026 เรื่องที่ 17 — เข้า URL ของเอกสารที่ไม่มีอยู่ (เช่น /DB9999-99999)
      ของเดิมยังเรนเดอร์ทั้งหน้าเหมือนมีเอกสารจริง — stepper เขียวครบ 3 ขั้น
      แถบเขียว "บันทึกข้อมูลแล้ว" เลขที่เอกสารตามที่พิมพ์ใน URL และปุ่มทุกปุ่มกดได้
      ผู้ใช้จึงเข้าใจว่ามีเอกสารฉบับนั้นจริง

      ตอนนี้ไม่มีข้อมูล = ไม่แสดงอะไรที่สื่อว่ามีเอกสารเลย เหลือแค่กล่องแจ้งและทางกลับ
    -->
    <div
      v-if="loadError"
      class="kt-portlet__body"
      data-test="fss-detail-notfound"
    >
      <div class="fss-detail__notfound">
        <i class="fas fa-file-excel fss-detail__notfound-icon"></i>
        <div class="fss-detail__notfound-title">ไม่พบเอกสารที่ต้องการ</div>
        <div class="fss-detail__notfound-text">{{ loadError }}</div>
        <div v-if="requestedDocumentNo" class="fss-detail__notfound-doc">
          เลขที่ที่เรียก: {{ requestedDocumentNo }}
        </div>
        <button
          type="button"
          class="btn btn-primary kt-margin-t-20"
          data-test="fss-detail-notfound-back"
          @click="goBackToList"
        >
          <i class="la la-arrow-left"></i> กลับไปที่รายการเอกสาร
        </button>
      </div>
    </div>

    <div v-else class="kt-portlet__body">
      <!-- begin: Stepper (บันทึกแล้ว — ครบทั้ง 3 ขั้น) -->
      <div class="row">
        <div class="col-12">
          <div class="fss-stepper">
            <div class="fss-step fss-step--done">
              <div class="fss-step__badge">&#10003;</div>
              <div class="fss-step__title">เลือก Filter</div>
            </div>
            <div class="fss-connector fss-connector--all-done"></div>
            <div class="fss-step fss-step--done">
              <div class="fss-step__badge">&#10003;</div>
              <div class="fss-step__title">ตรวจสอบข้อมูล</div>
            </div>
            <div class="fss-connector fss-connector--all-done"></div>
            <div class="fss-step fss-step--done">
              <div class="fss-step__badge">&#10003;</div>
              <div class="fss-step__title">บันทึกแล้ว</div>
            </div>
          </div>
        </div>
      </div>
      <!-- end: Stepper -->

      <!-- begin: หัวเอกสาร -->
      <div class="row">
        <div class="col-12">
          <div class="alert alert-success" role="alert">
            บันทึกข้อมูลแล้ว — ข้อมูลในเอกสารนี้ถูกล็อคเป็น snapshot ณ วันที่บันทึก
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-xl-4 col-lg-4 col-md-6 col-sm-12">
          <div class="form-group mb-3">
            <label class="mb-1">เลขที่เอกสาร:</label>
            <div class="fss-readonly kt-font-bold">{{ documentNo || "-" }}</div>
          </div>
        </div>
        <div class="col-xl-4 col-lg-4 col-md-6 col-sm-12">
          <div class="form-group mb-3">
            <label class="mb-1">วันที่บันทึก:</label>
            <div class="fss-readonly">{{ snapshotDateText }}</div>
          </div>
        </div>
        <!-- FS-118 — ตัดช่อง "ตัวแทนจำหน่าย:" (FS-096) ออก : แสดงอยู่ใน Filter ที่เลือกแล้ว (ผู้ใช้สั่ง 16/10/2026) -->
      </div>
      <!-- begin: Filter ที่เลือก -->
      <!--
        FS-118 (16/10/2026) — หน้าตาเดียวกับขั้นที่ 2 (FormSummaryStockPreview.vue "Filter ที่เลือก")
          หัวข้อ h5 + ตาราง ป้าย | ค่า ทีละ filter (แทนรูปแบบ FS-076 ป้ายซ้าย + บรรทัด "+ ...")
          ข้อมูลจาก filterLines ของ API : บรรทัดแรก "ป้าย — ค่า" · บรรทัดถัดไป "ป้าย: ค่า"
          บรรทัดสาขาอยู่ใต้ชื่อตัวแทนในตาราง (FS-116)
      -->
      <div class="row">
        <div class="col-12" data-test="fss-detail-filter">
          <h5 class="kt-font-bold kt-margin-b-10">Filter ที่เลือก</h5>
          <table v-if="filterItems.length > 0" class="table table-sm fss-filter-table">
            <tbody>
              <tr
                v-for="(item, index) in filterItems"
                :key="index"
                data-test="fss-detail-filter-line"
              >
                <td class="fss-filter-table__label">{{ item.label }}</td>
                <td>{{ item.text }}</td>
              </tr>
            </tbody>
          </table>
          <div v-else-if="filterText" class="fss-filter-text">{{ filterText }}</div>
          <div v-else class="text-muted">-</div>
        </div>
      </div>
      <!-- end: Filter ที่เลือก -->
      <!-- end: หัวเอกสาร -->

      <div class="kt-separator kt-separator--border-dashed kt-separator--space-sm"></div>

      <!-- begin: ไม่พบข้อมูล -->
      <div v-if="isNoData" class="row">
        <div class="col-12">
          <div class="alert alert-secondary text-center" role="alert">
            <div class="kt-font-bold">ไม่พบข้อมูล</div>
            <div class="text-muted">{{ noDataMessage }}</div>
          </div>
        </div>
      </div>
      <!-- end: ไม่พบข้อมูล -->

      <!-- begin: Summary table (อ่านจาก snapshot ที่บันทึกไว้) -->
      <div v-else>
        <div class="row">
          <div class="col-lg-12 table-responsive">
            <!--
              FS-116 — โครงเดียวกับขั้นที่ 2 แบบ 1 ราย (FormSummaryStockPreview.vue · FS-108)
                ตารางระดับตัวแทน 1 แถว (บรรทัดสาขาใต้ชื่อ) + แถวรายละเอียดสินค้ากางไว้ตลอด
                ตัวเลขจาก snapshot ชุดเดิม — จัดวางใหม่อย่างเดียว
                ไม่มี Grand Total ชั้นนอก — ดูได้ทีละ 1 ตัวแทน ยอดซ้ำกับแถวตัวแทน (ผู้ใช้สั่ง 16/10/2026)
            -->
            <table
              class="table table-sm table-bordered fss-agent-table"
              data-test="fss-detail-agent-table"
            >
              <thead>
                <tr>
                  <th class="text-center" style="width: 8%;">ลำดับ</th>
                  <th class="text-center">ชื่อตัวแทนจำหน่าย</th>
                  <th class="text-center" style="width: 16%;">จำนวนคงค้าง</th>
                  <th class="text-center" style="width: 22%;">ยอดรวม (ก่อน VAT)</th>
                </tr>
              </thead>
              <tbody>
                <tr data-test="fss-detail-agent-row">
                  <td class="text-center">1</td>
                  <td>
                    {{ agentLabel }}
                    <div
                      v-if="branchLine"
                      class="fss-branch-line"
                      data-test="fss-detail-branch-line"
                    >
                      {{ branchLine }}
                    </div>
                  </td>
                  <td class="text-right">{{ formatQty(totalQty) }}</td>
                  <td class="text-right">{{ formatAmount(totalAmount) }}</td>
                </tr>

                <tr class="fss-detail-row" data-test="fss-detail-detail-row">
                  <td colspan="4">
                    <div class="fss-detail-box">
                      <div class="fss-detail-box__title" data-test="fss-detail-detail-title">
                        รายละเอียดสินค้าคงค้าง — {{ agentNameOnly }}
                      </div>
                      <div class="table-responsive">
                        <table class="table table-sm table-striped table-bordered">
                          <thead>
                            <tr>
                              <th class="text-center" style="width: 7%;">ลำดับ</th>
                              <th class="text-center" style="width: 18%;">ประเภทสินค้า</th>
                              <th class="text-center" style="width: 15%;">รหัสสินค้า</th>
                              <th class="text-center" style="width: 27%;">ชื่อสินค้า</th>
                              <th class="text-center" style="width: 12%;">จำนวนคงค้าง</th>
                              <th class="text-center" style="width: 8%;">หน่วย</th>
                              <th class="text-center" style="width: 13%;">
                                จำนวนเงิน (ก่อน VAT)
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(row, index) in details" :key="index">
                              <td class="text-center">{{ row.seqNo }}</td>
                              <td>{{ row.productTypeName }}</td>
                              <td>{{ row.productCode }}</td>
                              <td>{{ row.productName }}</td>
                              <td class="text-right">{{ formatQty(row.qty) }}</td>
                              <td class="text-center">{{ row.unitName }}</td>
                              <td class="text-right">{{ formatAmount(row.amount) }}</td>
                            </tr>
                          </tbody>
                          <tfoot>
                            <tr class="fss-total-row">
                              <!-- FS-036 ข้อ 8 — mockup ขั้นที่ 4 ของ spec ฉบับเซ็น (L3087) ใช้คำว่า "Grand Total"
                                   เหมือนขั้นที่ 3 (เดิมเป็น "รวมทั้งสิ้น") -->
                              <td colspan="4" class="text-right kt-font-bold">Grand Total</td>
                              <td class="text-right kt-font-bold">{{ formatQty(totalQty) }}</td>
                              <td class="text-center">—</td>
                              <td class="text-right kt-font-bold">
                                {{ formatAmount(totalAmount) }}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
              <!--
                FS-116 — ไม่มีแถว Grand Total ชั้นนอก : มีตัวแทนแค่ 1 ราย ยอดซ้ำกับแถวตัวแทน
                  และ Grand Total ของตารางรายละเอียด (ผู้ใช้สั่ง 16/10/2026)
              -->
            </table>
          </div>
        </div>

        <!--
          FS-047 — ตัดกล่องสรุป (จำนวนสินค้ารวม / จำนวนเงินรวม / ตัวอักษร) ออกแล้ว
            ซ้ำซ้อนกับแถว Grand Total ใน <tfoot> ของตารางด้านบน
            และ mockup หน้าจอของ spec ก็จบที่แถว Grand Total แล้วต่อด้วยปุ่มทันที
            ทั้งขั้นที่ 3 และขั้นที่ 4 — กล่องสรุปมีเฉพาะใน "ฟอร์ม PDF" ซึ่งคนละอย่างกับหน้าจอ
          CSS .fss-summary* และ computed totalAmountText คงไว้ เผื่อกลับคำ
        -->
      </div>
      <!-- end: Summary table -->

      <div class="kt-separator kt-separator--border-dashed kt-separator--space-sm"></div>

      <!-- begin: Actions -->
      <!--
        FS-048 — แถวขวายาวขึ้นเพราะบรรทัดวันหมดอายุย้ายเข้ามาอยู่ในแถวเดียวกับปุ่ม
          ถ้าคงสัดส่วน 6/6 ไว้ ปุ่มลายเซ็นจะตกบรรทัดแล้วล้นออกนอกกรอบการ์ด
          จึงแบ่งเป็น 3/9 — ฝั่งซ้ายมีปุ่มเดียว ไม่ต้องใช้ครึ่งจอ
      -->
      <div class="row">
        <div class="col-xl-3">
          <!-- FS-036 ข้อ 4 — spec เขียนว่า "← กลับไปที่รายการเอกสาร"
               ลูกศรใช้ไอคอน la-arrow-left ตัวเดิม ไม่พิมพ์ "←" ซ้ำลงไปในข้อความ -->
          <button
            type="button"
            class="btn btn-sm btn-clean kt-margin-r-10"
            data-test="fss-detail-back"
            @click="goBackToList"
          >
            <i class="la la-arrow-left"></i>
            <span class="kt-hidden-mobile">กลับไปที่รายการเอกสาร</span>
          </button>
        </div>
        <div class="col-xl-9 text-right">
          <!--
            FS-048 — บรรทัดวันหมดอายุย้ายมาอยู่ "หน้าปุ่มพิมพ์ PDF" ตามที่ผู้ใช้สั่ง
            (เดิมอยู่ใต้แถวปุ่ม) · สีและโครง span ห้ามเปลี่ยน — DT-1 วัดค่าสีไว้
            FS-029 — คำเตือนสีแดง ส่วนวันที่สีเข้ม จะได้อ่านวันที่ง่าย ไม่จมไปกับสีแดง
              ซ่อนเฉพาะใบเก่าที่ยังไม่ได้ stamp วันหมดอายุ (ExcelExpireDate = NULL)
            FS-094 — สีปุ่มทั้งแถว (PDF / Excel / แนบไฟล์) ใช้ชุดเดียวกับปุ่มวงกลมหน้ารายการ
              (.fss-btn--* ใน form-summary-stock-actions.css) แทนสีของ Metronic
          -->
          <button
            type="button"
            class="btn btn-sm fss-btn fss-btn--orange kt-margin-r-10"
            data-test="fss-detail-pdf"
            @click="exportPdf"
          >
            <i class="la la-print"></i>
            <span class="kt-hidden-mobile">พิมพ์ PDF</span>
          </button>
          <!--
            FS-029 — ปุ่มเทาและกดไม่ได้เมื่อรายละเอียดบาร์โค้ดหมดอายุ/ถูก purge แล้ว
              ตั้งใจไม่ใช้ attribute `disabled` เพราะปุ่มที่ disabled จริงจะไม่ส่ง event
              ตอน hover → tooltip (title) ไม่ขึ้น แต่ spec บังคับว่าต้องมี tooltip อธิบายเหตุผล
              จึงใช้ aria-disabled + ทำให้เป็นสีเทา แล้วกันการกดจริงที่ method exportExcel()
              (แพตเทิร์นเดียวกับ FormSummaryStockList.vue)
            FS-036 ข้อ 5 — ข้อความปุ่มตาม spec: "Export Excel (รายละเอียดบาร์โค้ด)"
          -->
          <!--
            QA 08/10/2026 เรื่องที่ 8 — ย้ายบรรทัด "หมดอายุ Export Excel"
            มาไว้ "ใต้ปุ่ม Export Excel" โดยตรง
            (เดิม FS-048 ให้ไปอยู่หน้าปุ่มพิมพ์ PDF — ผู้ใช้เปลี่ยนใจรอบนี้
             เพราะคำเตือนไปปนกับปุ่มอื่นจนแยกไม่ออกว่าเป็นคำเตือนหรือปุ่ม)
            จับปุ่มกับคำเตือนไว้ในกล่องเดียวกัน ผู้ใช้จะเห็นว่าสองอย่างนี้คู่กัน
            ⚠ คง data-test + คลาส span เดิมไว้ — DT-1 วัดค่าสีจากคลาสพวกนี้
          -->
          <span class="fss-detail__excel-group kt-margin-r-10">
            <button
              type="button"
              class="btn btn-sm fss-btn"
              :class="excelAvailable ? 'fss-btn--green' : 'fss-btn--disabled'"
              :aria-disabled="excelAvailable ? 'false' : 'true'"
              :title="excelTitle"
              data-test="fss-detail-excel"
              @click="exportExcel"
            >
              <i class="la la-file-excel-o"></i>
              <span class="kt-hidden-mobile">Export Excel (รายละเอียดบาร์โค้ด)</span>
            </button>
            <span
              v-if="excelExpireDateText"
              class="fss-expire-line fss-detail__expire-under"
              data-test="fss-detail-expire-line"
            >
              <span class="fss-expire-warn">⚠ หมดอายุ Export Excel</span>
              <span class="fss-expire-date">{{ excelExpireDateText }}</span>
            </span>
          </span>
          <!--
            FS-050 — สีปุ่มแนบไฟล์ต้องเป็นชุดเดียวกับหน้ารายการเอกสาร
              เขียวเข้ม = มีไฟล์แล้ว · แดง = ยังไม่มีไฟล์
            สีในหน้ารายการ "สื่อสถานะ" ไม่ใช่สีตกแต่ง จึงยกเงื่อนไขมาทั้งชุด
            ไม่ใช่ทาสีเขียวเฉย ๆ ไม่งั้นสีเดียวกันจะหมายคนละอย่างใน 2 หน้า
            FS-094 — ใช้ค่าสีจริงชุดเดียวกับปุ่มวงกลมหน้ารายการ (เขียว #10B981 / แดง #EF4444)
              .fss-attach-has คงไว้เป็นหมุดบอกสถานะ "มีไฟล์" แบบเดียวกับหน้ารายการ ไม่มีสีของตัวเอง
          -->
          <button
            type="button"
            class="btn btn-sm fss-btn kt-margin-r-10"
            :class="hasAttachment ? 'fss-btn--green fss-attach-has' : 'fss-btn--red'"
            data-test="fss-detail-attachment"
            @click="openAttachment"
          >
            <i class="la la-paperclip"></i>
            <span class="kt-hidden-mobile">
              แนบไฟล์เอกสาร ({{ attachmentCount }})
            </span>
          </button>
          <!--
            FS-059 — ตัดปุ่ม "ลายเซ็นผู้จัดทำ" และ thumbnail ออกจากหน้านี้แล้ว

            spec ระบุว่าปุ่มนี้อยู่ "ในหน้าตรวจสอบข้อมูลก่อนบันทึก" เท่านั้น
            และ mockup ขั้นที่ 4 มีแค่ 4 อย่าง — กลับไปที่รายการเอกสาร / หมดอายุ Export Excel /
            พิมพ์ PDF / Export Excel / แนบไฟล์เอกสาร — ไม่มีปุ่มลายเซ็น

            เหตุผลเชิงตรรกะ: เอกสารที่บันทึกแล้วลายเซ็นถูกตรึงถาวร เปลี่ยนไม่ได้อีก
            (modal เองก็เขียนว่า "เปลี่ยนไม่ได้อีก หากต้องการใช้ลายเซ็นอื่น ต้องสร้างเอกสารใหม่")
            ปุ่มบนหน้านี้จึงเปิด popup ที่ทำอะไรไม่ได้เลย

            ⚠ ด่านสิทธิ์ของกลุ่ม B (FS-018) ย้ายไปเฝ้าที่หน้า Preview / GroupPreview แทน
              ดู PM-7 / PM-7b ใน formsummarystock-permission.spec.js
              (ถ้าเฝ้าที่หน้านี้ต่อ จะผ่านโดยไม่ได้พิสูจน์อะไร เพราะไม่มีปุ่มให้เห็นอยู่แล้ว)

            computed canManageSignature / hasSignature / signatureImageUrl / signatureButtonText
            และ modal ลายเซ็นท้ายไฟล์ยังคงไว้ เผื่อกลับคำ
          -->

          <!--
            FS-049 — ตัดบรรทัด "ข้อมูลระดับบาร์โค้ด N รายการอยู่ในไฟล์ Excel" ออกตามที่ผู้ใช้สั่ง
            ⚠ ข้อนี้ต่างจาก spec โดยตั้งใจ — mockup ขั้นที่ 4 มีบรรทัดนี้อยู่ (เดิมคือ FS-036 ข้อ 5)
              บันทึกไว้ในหัวข้อ 6 ของ CR-Features.md แล้ว รอ BA ยืนยันย้อนหลัง
            computed excelNoteText ยังคงไว้ เผื่อกลับคำ — ไม่มีที่ไหนเรียกใช้แล้ว

            FS-048 — บรรทัดวันหมดอายุย้ายขึ้นไปอยู่หน้าปุ่ม "พิมพ์ PDF" แล้ว (ดูด้านบน)
          -->
        </div>
      </div>
      <!-- end: Actions -->
    </div>

    <!-- F08 — popup แนบไฟล์เอกสาร · ปิดแล้ว loadDocument ใหม่เพื่ออัปเดตจำนวนไฟล์บนปุ่ม -->
    <form-summary-stock-attachment-modal
      ref="attachmentModal"
      @closed="loadDocument"
    />

    <!-- F15 — popup ลายเซ็นผู้จัดทำ 2 แถบ -->
    <FormSummaryStockSignatureModal ref="signatureModal" />
  </div>
</template>

<script>
import axios from "axios";
// Action Button Design System + Stepper ของโมดูลนี้ (QA เรื่องที่ 11 + 16)
import "./form-summary-stock-actions.css";
// FS-088 — tooltip ของปุ่มวงกลมเป็นแบบ Bootstrap (ผูกครั้งเดียวทั้งโมดูล เรียกซ้ำได้)
import { installFssTooltips } from "./form-summary-stock-tooltips";
installFssTooltips();
import moment from "moment";
import router from "../../router";
import FormSummaryStockAttachmentModal from "./FormSummaryStockAttachmentModal";
import FormSummaryStockSignatureModal from "./FormSummaryStockSignatureModal";

require("sweetalert2");

// ─────────────────────────────────────────────────────────────────────────────
// CR-FormSummaryStock F05 — ขั้นที่ 3 "บันทึกแล้ว" (/PrintSummary/Barcodes/:documentNo)
//
// อ่านจาก snapshot ที่บันทึกไว้เท่านั้น (POST /Api/FormSummaryStock/Document)
// ไม่คำนวณใหม่ — ตัวเลขต้องตรงกับตอนกดบันทึกเสมอ
//
// route นี้เป็นลูกของ /PrintSummary/Barcodes (FormSummaryStockList.vue) แบบเดียวกับ /New
// จึงเข้าถึงได้ทั้งจากปุ่ม "ดูรายละเอียด" ในตาราง (FormSummaryStockList.vue:264)
// และจากขั้นที่ 2 หลังบันทึกสำเร็จ
//
// SweetAlert2 = ^8.2.3 (SBP.Web/package.json) → ใช้ option ชื่อ `type` (ไม่ใช่ `icon` ของ v9+)
// ─────────────────────────────────────────────────────────────────────────────

// ปุ่มที่ยังไม่ถึงคิวพัฒนา — แจ้งผู้ใช้เป็นภาษาไทยแทนการเงียบ (ห้ามใช้ alert()/console.log)
// รูปแบบเดียวกับ FormSummaryStockList.vue:showUnderDevelopment
function showUnderDevelopment(featureName) {
  Swal.fire("อยู่ระหว่างพัฒนา", "ฟังก์ชัน " + featureName + " อยู่ระหว่างพัฒนา", "info");
}

// FS-029 — ข้อความ tooltip ของปุ่ม Excel เมื่อรายละเอียดบาร์โค้ดหมดอายุ
//   คำต่อคำเดียวกับหน้ารายการ (FormSummaryStockList.vue : EXCEL_EXPIRED_TOOLTIP)
//   ห้ามเขียนใหม่ให้ต่างกัน — ผู้ใช้เห็นทั้ง 2 หน้าและต้องได้คำอธิบายชุดเดียวกัน
const EXCEL_EXPIRED_TOOLTIP =
  "ไม่สามารถ Export ข้อมูลได้ เนื่องจากข้อมูลรายละเอียดบาร์โค้ดถูกลบไปแล้ว (ระบบเก็บข้อมูล 2 ปีนับจากวันบันทึกเอกสาร)";

// FS-035 — หน้าชั่วคราวในแท็บใหม่ระหว่างรอ BIRT สร้างไฟล์
//   เขียนเป็นสตริงบรรทัดเดียว ไม่มี \n โดยตั้งใจ (ไฟล์ .vue นี้เคยเจอปัญหา escape ของ \n
//   จนทำให้ bundle พัง — ดูคอมเมนต์เรื่อง regex ที่ exportExcel)
const PDF_PREVIEW_PLACEHOLDER =
  '<!DOCTYPE html><html lang="th"><head><meta charset="utf-8">' +
  "<title>กำลังเตรียมไฟล์ PDF</title></head>" +
  '<body style="font-family: sans-serif; padding: 24px; color: #48465b;">' +
  "กำลังเตรียมไฟล์ PDF กรุณารอสักครู่...</body></html>";

// อายุของ blob URL ก่อนถูกคืนหน่วยความจำ — ต้องนานพอให้แท็บใหม่โหลดไฟล์เสร็จ
// (เพิกถอนเร็วเกินไป = แท็บที่เพิ่งเปิดจะกลายเป็นหน้าว่าง)
const BLOB_URL_TTL_MS = 60000;

export default {
  computed: {
    /**
     * QA เรื่องที่ 17 — เลขที่เอกสารที่ผู้ใช้พิมพ์มาใน URL
     * ใช้แสดงในหน้า "ไม่พบเอกสาร" เพื่อให้รู้ว่าเรียกอะไรไปแล้วไม่เจอ
     * อ่านจาก route ไม่ใช่จาก document เพราะกรณีนี้ document เป็น null
     */
    requestedDocumentNo: function() {
      return (this.$route && this.$route.params && this.$route.params.documentNo) || "";
    },
    // FormSummaryStock_Header.Id — route ส่งมาแค่ documentNo จึงต้องรอ API ตอบก่อน
    // ใช้เปิด popup แนบไฟล์ (F08) และ popup ลายเซ็น (F15) ซึ่งทั้งคู่อ้างอิงด้วย Id ไม่ใช่เลขที่เอกสาร
    headerId: function() {
      return this.document && this.document.id ? this.document.id : 0;
    },
    documentNo: function() {
      return this.document && this.document.documentNo
        ? this.document.documentNo
        : this.$route.params.documentNo;
    },
    snapshotDateText: function() {
      if (!this.document || !this.document.snapshotDate) return "-";
      return moment(this.document.snapshotDate).format("DD/MM/YYYY HH:mm");
    },
    agentName: function() {
      return this.document ? this.document.agentName : "";
    },
    // FS-096 — "[รหัส] ชื่อ" แบบเดียวกับหน้ารายการ (FormSummaryStockList.vue คอลัมน์ตัวแทน)
    agentLabel: function() {
      var code = this.document ? this.document.agentCode : "";
      var name = this.agentName;
      if (code && name) return "[" + code + "] " + name;
      if (code) return "[" + code + "]";
      return name || "-";
    },
    filterText: function() {
      return this.document ? this.document.filterText : "";
    },
    // FS-076 — [0] filter บังคับ · [1..] filter ไม่บังคับทีละตัว (ไม่มี "+ " นำหน้า — เติมตอนแสดง)
    filterLines: function() {
      return this.document && this.document.filterLines ? this.document.filterLines : [];
    },
    // FS-116 — หัวแถวรายละเอียด ไม่มี [รหัส] (แบบขั้นที่ 2 · FS-106/107)
    agentNameOnly: function() {
      return this.agentName || (this.document ? this.document.agentCode : "");
    },
    // FS-118 — แยก filterLines เป็น { label, text } ให้แสดงเป็นตารางแบบขั้นที่ 2
    //   server ประกอบบรรทัดแรกด้วย " — " และ filter ไม่บังคับด้วย ": " (GetFormSummaryStockDocumentQueryHandler)
    filterItems: function() {
      return this.filterLines.map(function(line, index) {
        var text = String(line);
        var sep = index === 0 ? " — " : ": ";
        var at = text.indexOf(sep);
        if (at < 0) return { label: "", text: text };
        return { label: text.substring(0, at), text: text.substring(at + sep.length) };
      });
    },
    details: function() {
      return this.document && this.document.details ? this.document.details : [];
    },
    totalQty: function() {
      return this.document ? this.document.totalQty : 0;
    },
    totalAmount: function() {
      return this.document ? this.document.totalAmount : 0;
    },
    totalAmountText: function() {
      return this.document ? this.document.totalAmountText : "";
    },
    attachmentCount: function() {
      return this.document && this.document.attachmentCount
        ? this.document.attachmentCount
        : 0;
    },
    /**
     * FS-050 — ใช้ตัดสินสีปุ่มแนบไฟล์ให้ตรงกับหน้ารายการเอกสาร
     *   เขียวเข้ม = มีไฟล์แล้ว · แดง = ยังไม่มีไฟล์
     * (FormSummaryStockList.vue:552 — เงื่อนไขเดียวกัน)
     */
    hasAttachment: function() {
      return this.attachmentCount > 0;
    },
    isNoData: function() {
      return this.details.length === 0;
    },
    noDataMessage: function() {
      if (this.loadError) return this.loadError;
      return "เอกสารนี้ไม่มีรายการสินค้าคงค้างที่บันทึกไว้";
    },

    // FS-012 — สถานะปุ่มลายเซ็น
    //   เอกสารบันทึกแล้ว "แนบแล้ว" จึงหมายถึงมีภาพตรึงไว้กับเอกสารฉบับนี้จริง
    //   (ตรึงตั้งแต่วันบันทึก เปลี่ยนไม่ได้อีก — ปุ่มจึงเป็นแค่ทางเข้าไปดูภาพ)
    // FS-017 — เอกสารที่ไม่ได้เลือกภาพเฉพาะกิจจะถูกพิมพ์ด้วยลายเซ็นค่าเริ่มต้นของระบบ
    //   ตาม spec (CR-Features.md:320) ถือว่ามีลายเซ็นแล้ว ปุ่มจึงต้องเขียวเช่นกัน
    hasSignature: function() {
      return !!this.signatureAttachedId || this.hasDefaultSignature;
    },
    signatureButtonText: function() {
      return this.hasSignature ? "ลายเซ็นผู้จัดทำ (ดูภาพ)" : "ลายเซ็นผู้จัดทำ";
    },

    // ── FS-029 — สถานะอายุ Export Excel (F09) ────────────────────────────────
    // แหล่งข้อมูลที่ถูกต้องคือ /Api/FormSummaryStock/Document (server คำนวณให้
    // ด้วยสูตรเดียวกับหน้ารายการ — GetFormSummaryStockDocumentQueryHandler)
    //
    // ระหว่างที่ response ของ endpoint นั้นยังไม่ส่ง 4 ฟิลด์นี้ออกมา (การเพิ่มฟิลด์
    // ต้องแก้ FormSummaryStockController ซึ่งอยู่นอกขอบเขตงานรอบนี้) จะใช้แถวของ
    // เอกสารใบเดียวกันจาก /Api/FormSummaryStock/Search แทน — ดู loadExcelExpiry()
    // ห้ามคำนวณจากวันที่ฝั่ง client เอง เพราะนาฬิกาเครื่องผู้ใช้เชื่อไม่ได้
    excelExpiry: function() {
      if (this.document && this.document.isExcelAvailable !== undefined) {
        return this.document;
      }
      return this.excelExpiryFallback;
    },
    // ไม่รู้สถานะ = ถือว่ายัง export ได้ เพื่อไม่ให้ใบที่ปกติถูกล็อคโดยไม่ตั้งใจ
    // (ด่านจริงอยู่ที่ ExportFormSummaryStockExcelCommandHandler อยู่แล้ว)
    excelAvailable: function() {
      const src = this.excelExpiry;
      return !src || src.isExcelAvailable !== false;
    },
    excelTitle: function() {
      return this.excelAvailable
        ? "Export Excel (รายละเอียดบาร์โค้ด)"
        : EXCEL_EXPIRED_TOOLTIP;
    },
    // แยกวันที่ออกจากข้อความเตือน เพราะ design ให้คนละสี (เหมือนหน้ารายการ)
    excelExpireDateText: function() {
      const src = this.excelExpiry;
      if (!src || !src.excelExpireDate) return "";
      return moment(src.excelExpireDate).format("DD/MM/YYYY");
    },

    // ── FS-036 ข้อ 5 — จำนวนรายการระดับบาร์โค้ดในไฟล์ Excel ───────────────────
    // ใช้ "จำนวนคงค้างรวม" ของเอกสารเป็นจำนวนบาร์โค้ด ไม่ใช่เลขที่เดาขึ้นมา:
    // FormSummaryStockAggregator.BuildRows กำหนด Qty = g.Count() คือ "จำนวนบาร์โค้ด
    // ในกลุ่ม (1 แถวจาก SP = 1 บาร์โค้ด)" → ผลรวมทุกแถว = จำนวนบาร์โค้ดทั้งใบ
    // ซึ่งเป็นชุดเดียวกับที่ ExportFormSummaryStockExcelCommandHandler เขียนลงไฟล์
    barcodeDetailCount: function() {
      return this.totalQty ? Number(this.totalQty) : 0;
    },
    excelNoteText: function() {
      // Export ไม่ได้แล้ว = ห้ามบอกว่ามี N รายการอยู่ในไฟล์ (ข้อมูลถูกลบไปแล้วจริง ๆ)
      if (!this.excelAvailable) return "";
      if (!this.barcodeDetailCount) return "";
      return (
        "ข้อมูลระดับบาร์โค้ด " +
        this.barcodeDetailCount.toLocaleString("en-US") +
        " รายการอยู่ในไฟล์ Excel"
      );
    },
  },
  data: function() {
    return {
      // ผลลัพธ์จาก /Api/FormSummaryStock/Document
      document: null,
      // ข้อความผิดพลาดจาก API (แสดงแทนตารางเปล่า)
      loadError: "",
      // F04 (D10) — บรรทัด "*สำนักงานใหญ่ + สาขา [ ... ]" ค่าว่าง = ไม่ต้องแสดง
      branchLine: "",
      // FS-012 — AttachedId ของลายเซ็นที่ตรึงไว้กับเอกสารฉบับนี้ (null = ยังไม่มีภาพ)
      //   /Api/FormSummaryStock/Document ไม่ได้คืนค่านี้มา จึงอ่านจาก endpoint Signature
      //   ตัวเดียวกับที่ popup ใช้ (ห้ามแก้ฝั่ง backend ในรอบนี้)
      signatureAttachedId: null,
      // FS-017 — ระบบมีลายเซ็นค่าเริ่มต้นหรือไม่ + url ของภาพที่เอกสารนี้ใช้จริง (signed URL อายุสั้น)
      hasDefaultSignature: false,
      signatureImageUrl: "",
      // FS-018 — สิทธิ์จัดการลายเซ็น (canManage จาก /Api/FormSummaryStock/Signature)
      //   ค่าเริ่มต้นต้องเป็น false เสมอ — ซ่อนปุ่มไว้ก่อนจนกว่า API จะยืนยันว่ามีสิทธิ์
      canManageSignature: false,
      // FS-029 — แถวของเอกสารใบนี้จากผลค้นหา (ใช้เฉพาะตอนที่ /Document ยังไม่ส่ง
      //   สถานะอายุ Excel มาเอง) null = ยังไม่รู้สถานะ → ปุ่มทำงานตามปกติ
      excelExpiryFallback: null,
    };
  },
  methods: {
    formatQty: function(value) {
      if (value == null || value === "") return "-";
      return Number(value).toLocaleString("en-US");
    },
    formatAmount: function(value) {
      if (value == null || value === "") return "-";
      return Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    loadDocument: function() {
      var self = this;
      var documentNo = self.$route.params.documentNo;

      if (!documentNo) {
        router.replace({ path: "/PrintSummary/Barcodes" });
        return;
      }

      // FS-029 — ล้างสถานะอายุ Excel ของใบก่อนหน้าทิ้งก่อนเสมอ
      //   (component ถูก reuse เมื่อเปลี่ยน :documentNo — ค้างไว้จะกลายเป็นสถานะของใบอื่น)
      self.excelExpiryFallback = null;

      KTApp.blockPage({
        opacity: 0.05,
        message: "กำลังโหลดข้อมูล กรุณารอสักครู่...",
      });

      axios
        .post(
          "/Api/FormSummaryStock/Document",
          { documentNo: documentNo },
          {
            headers: {
              Authorization: "Bearer " + self.$root.user.access_token,
            },
          }
        )
        .then(function(response) {
          if (response.data.success) {
            self.loadError = "";
            self.document = response.data;
            // headerId เพิ่งมีค่าหลังบรรทัดนี้ จึงโหลดบรรทัดสาขาต่อได้
            self.loadBranchLine();
            // FS-012 — สถานะลายเซ็นใช้กำหนดสี/ข้อความบนปุ่ม ต้องรู้ตั้งแต่เปิดหน้า
            self.loadSignatureState();
            // FS-029 — สถานะอายุ Export Excel ต้องรู้ก่อนผู้ใช้กดปุ่ม
            self.loadExcelExpiry();
          } else {
            self.document = null;
            self.loadError = response.data.message || "ไม่พบเอกสารที่ต้องการ";
            // QA เรื่องที่ 17 ข้อ 5 — เดิมหัวเรื่อง "ไม่พบข้อมูล" + เนื้อหา "ไม่พบใบงาน..."
            //   อ่านต่อกันเป็น "ไม่พบข้อมูลไม่พบใบงาน" · หน้าจอบอกรายละเอียดอยู่แล้ว
            //   popup จึงเหลือแค่ประโยคเดียว ไม่ต้องย้ำซ้ำ
            //   FS-080 — กด OK (หรือปิด popup ทางใดก็ได้) แล้วกลับหน้ารายการทันที
            //     ผู้ใช้เลือกทางแรกของรายงาน "popup error + redirect กลับ Screen 1 เมื่อ user กด OK"
            //     หน้าแจ้งไม่พบด้านหลังยังอยู่ระหว่าง popup เปิด — จอไม่ว่าง และไม่มีปุ่มหลอกกลับมา
            Swal.fire("ไม่พบเอกสาร", self.loadError, "warning").then(self.goBackToList);
          }
        })
        .catch(function(error) {
          var errorMsg =
            error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : "เกิดข้อผิดพลาด";
          self.document = null;
          self.loadError = errorMsg;
          // FS-080 — กติกาเดียวกับกรณีไม่พบเอกสาร
          Swal.fire("เกิดข้อผิดพลาด", errorMsg, "error").then(self.goBackToList);
        })
        .then(function() {
          KTApp.unblockPage();
        });
    },
    goBackToList: function() {
      router.push({ path: "/PrintSummary/Barcodes" });
    },
    // F06 (E1) — พิมพ์ฟอร์ม PDF จาก snapshot ผ่าน BIRT
    //   ⚠ template ใช้ TH Sarabun New ชั่วคราว — spec ต้องการ Singha Water (Q7 ยังไม่ได้ไฟล์)
    //
    // FS-035 — ปุ่มนี้คือ "เปิดดูก่อนพิมพ์" (spec §F) ไม่ใช่ปุ่มดาวน์โหลด
    //   ของเดิมสร้าง <a download> แล้ว .click() → ไฟล์ลงเครื่องทันที ผู้ใช้ไม่ได้ดูก่อน
    //   ตอนนี้เปิดไฟล์ในแท็บใหม่แทน แล้วให้ผู้ใช้สั่งพิมพ์/บันทึกจากตัวอ่าน PDF ของเบราว์เซอร์เอง
    exportPdf: function() {
      var self = this;

      if (!self.headerId) {
        Swal.fire("เกิดข้อผิดพลาด", "ไม่พบเลขที่อ้างอิงของเอกสารนี้", "error");
        return;
      }

      // ── เปิดแท็บ "เดี๋ยวนี้" ขณะยังอยู่ใน user gesture ของการคลิก ──────────
      //   ถ้ารอไปเปิดตอน axios ตอบกลับ เบราว์เซอร์จะถือว่าเป็น popup ที่ไม่ได้มาจาก
      //   การกระทำของผู้ใช้แล้วบล็อกทิ้งเงียบ ๆ (ผู้ใช้จะเห็นแค่ "กดแล้วไม่มีอะไรเกิดขึ้น")
      var previewTab = window.open("", "_blank");
      if (previewTab && previewTab.document) {
        previewTab.document.write(PDF_PREVIEW_PLACEHOLDER);
        previewTab.document.close();
      }

      // FS-133 — เปิดแท็บได้ → ขอตั๋วแล้วชี้แท็บไปที่ URL จริง (PdfView/{ตั๋ว}/{ชื่อไฟล์}.pdf)
      //   แถบที่อยู่และชื่อไฟล์ตอนกดดาวน์โหลดในตัวดู PDF จึงเป็นชื่อเอกสาร ไม่ใช่ GUID ของ blob:
      //   โดน popup blocker (ไม่มีแท็บ) → ใช้ทางเดิมด้านล่าง : ดึงไฟล์แล้วเสนอให้ดาวน์โหลดแทน
      //   หน้ารายการ (FormSummaryStockList.vue) ใช้กติกาเดียวกัน — แก้ที่ไหนต้องแก้คู่กัน
      if (previewTab) {
        self.openPdfByTicket(previewTab);
        return;
      }

      KTApp.blockPage({ opacity: 0.05, message: "กำลังสร้างไฟล์ PDF กรุณารอสักครู่..." });

      axios({
        method: "post",
        url: "/Api/FormSummaryStock/PrintPdf",
        responseType: "blob",
        data: { headerId: self.headerId },
        headers: { Authorization: "Bearer " + self.$root.user.access_token },
      })
        .then(function(response) {
          var contentType = response.headers["content-type"] || "";

          // server ปฏิเสธ (ไม่มีสิทธิ์ / render ไม่สำเร็จ) จะคืน JSON ไม่ใช่ไฟล์
          if (contentType.indexOf("application/json") !== -1) {
            self.closePreviewTab(previewTab);

            var reader = new FileReader();
            reader.onload = function() {
              var message = "พิมพ์เอกสารไม่สำเร็จ";
              try {
                var parsed = JSON.parse(reader.result);
                if (parsed && parsed.message) message = parsed.message;
              } catch (e) {
                // ใช้ข้อความเริ่มต้น
              }
              Swal.fire("พิมพ์ไม่ได้", message, "warning");
            };
            reader.readAsText(response.data);
            return;
          }

          var filename = "";
          var disposition = response.headers["content-disposition"];
          if (disposition && disposition.indexOf("attachment") !== -1) {
            var marker = "filename=";
            var at = disposition.indexOf(marker);
            if (at !== -1) {
              filename = disposition.substring(at + marker.length);
              var semi = filename.indexOf(";");
              if (semi !== -1) filename = filename.substring(0, semi);
              filename = filename.trim().replace(/^["']|["']$/g, "");
            }
          }

          // บังคับ MIME เป็น application/pdf — บาง proxy คืน content-type เป็น
          // octet-stream ซึ่งจะทำให้แท็บใหม่ "ดาวน์โหลด" แทนที่จะ "เปิดดู"
          var pdfBlob = new Blob([response.data], { type: "application/pdf" });
          var blobUrl = window.URL.createObjectURL(pdfBlob);
          var fileName = decodeURIComponent(filename) || "เอกสาร.pdf";

          if (previewTab && !previewTab.closed) {
            previewTab.location.href = blobUrl;
          } else {
            // โดน popup blocker — ต้องบอกผู้ใช้ให้รู้ ไม่ใช่เงียบ
            // และต้องมีทางออกให้ด้วย (ดาวน์โหลดแทน) ไม่ใช่บอกว่าทำไม่ได้เฉย ๆ
            Swal.fire({
              title: "เบราว์เซอร์บล็อกการเปิดแท็บใหม่",
              text:
                "ไฟล์ PDF สร้างเสร็จแล้ว แต่เบราว์เซอร์ไม่อนุญาตให้เปิดแท็บใหม่ " +
                "กรุณาอนุญาต popup ของเว็บนี้แล้วกดพิมพ์อีกครั้ง หรือกดปุ่มด้านล่างเพื่อดาวน์โหลดไฟล์แทน",
              type: "warning",
              showCancelButton: true,
              confirmButtonText: "ดาวน์โหลดไฟล์แทน",
              cancelButtonText: "ปิด",
            }).then(function(result) {
              if (!result || !result.value) return;
              var link = document.createElement("a");
              link.href = blobUrl;
              link.download = fileName;
              link.click();
            });
          }

          // คืนหน่วยความจำของ blob — หน่วงไว้ให้แท็บใหม่ (หรือการดาวน์โหลด) โหลดไฟล์เสร็จก่อน
          setTimeout(function() {
            window.URL.revokeObjectURL(blobUrl);
          }, BLOB_URL_TTL_MS);
        })
        .catch(function() {
          self.closePreviewTab(previewTab);
          Swal.fire("เกิดข้อผิดพลาด", "พิมพ์เอกสารไม่สำเร็จ กรุณาลองใหม่อีกครั้ง", "error");
        })
        .then(function() {
          KTApp.unblockPage();
        });
    },
    // FS-133 — ขอตั๋วเปิด PDF (ตรวจสิทธิ์ชุดเดียวกับ PrintPdf) แล้วเปิดในแท็บที่เตรียมไว้
    //   ตั๋วอายุ 5 นาที — หมดอายุแล้วแท็บจะแสดงหน้าแจ้งพร้อมปุ่ม "ไปที่เอกสาร" (server ทำ)
    openPdfByTicket: function(previewTab) {
      var self = this;

      KTApp.blockPage({ opacity: 0.05, message: "กำลังเปิดเอกสาร PDF..." });

      axios({
        method: "post",
        url: "/Api/FormSummaryStock/PdfTicket",
        data: { headerId: self.headerId },
        headers: { Authorization: "Bearer " + self.$root.user.access_token },
      })
        .then(function(response) {
          var data = response.data || {};
          if (!data.success || !data.url) {
            self.closePreviewTab(previewTab);
            Swal.fire("พิมพ์ไม่ได้", data.message || "พิมพ์เอกสารไม่สำเร็จ", "warning");
            return;
          }
          if (!previewTab.closed) previewTab.location.href = data.url;
        })
        .catch(function() {
          self.closePreviewTab(previewTab);
          Swal.fire("เกิดข้อผิดพลาด", "พิมพ์เอกสารไม่สำเร็จ กรุณาลองใหม่อีกครั้ง", "error");
        })
        .then(function() {
          KTApp.unblockPage();
        });
    },
    // FS-035 — ปิดแท็บที่เปิดค้างไว้เมื่อสร้างไฟล์ไม่สำเร็จ
    //   ปล่อยทิ้งไว้ผู้ใช้จะเจอแท็บว่างค้าง แล้วเข้าใจผิดว่าระบบแฮงก์
    closePreviewTab: function(previewTab) {
      if (previewTab && !previewTab.closed) {
        try {
          previewTab.close();
        } catch (e) {
          // ปิดไม่ได้ก็ไม่เป็นไร — ข้อความแจ้งเตือนอยู่ที่แท็บหลักอยู่แล้ว
        }
      }
    },
    // F07 (E3) — Export Excel รายละเอียดบาร์โค้ด 46 คอลัมน์ จาก snapshot ของเอกสารนี้
    exportExcel: function() {
      const self = this;

      // FS-029 — หมดอายุแล้วต้องกดไม่ได้จริง ไม่ใช่แค่ทำให้ปุ่มดูจาง
      //   (tooltip บนปุ่มอธิบายเหตุผลอยู่แล้ว จึงไม่เด้ง popup ซ้ำ — เหมือนหน้ารายการ)
      if (!self.excelAvailable) return;

      if (!self.headerId) {
        Swal.fire("เกิดข้อผิดพลาด", "ไม่พบเลขที่อ้างอิงของเอกสารนี้", "error");
        return;
      }

      KTApp.blockPage({
        opacity: 0.05,
        message: "กำลังสร้างไฟล์ Excel กรุณารอสักครู่...",
      });

      axios({
        method: "post",
        url: "/Api/FormSummaryStock/ExportExcel",
        responseType: "arraybuffer",
        data: { headerId: self.headerId },
        headers: {
          Authorization: "Bearer " + self.$root.user.access_token,
        },
      })
        .then(function(response) {
          const contentType = response.headers["content-type"] || "";

          // server ปฏิเสธ (หมดอายุ / ไม่มีสิทธิ์ / ไม่มีข้อมูล) จะคืน JSON ไม่ใช่ไฟล์
          // ตัว body ยังเป็น arraybuffer จึงต้อง decode ก่อน ไม่งั้น popup จะว่างเปล่า
          if (contentType.indexOf("application/json") !== -1) {
            let message = "ไม่สามารถ Export Excel ได้";
            try {
              const parsed = JSON.parse(
                new TextDecoder("utf-8").decode(new Uint8Array(response.data))
              );
              if (parsed && parsed.message) message = parsed.message;
            } catch (e) {
              // ปล่อยข้อความเริ่มต้นไว้
            }
            Swal.fire("ไม่สามารถ Export ได้", message, "warning");
            return;
          }

          // ชื่อไฟล์ถูก encode เป็น %20 ฝั่ง server แล้ว decodeURIComponent จึงคืนช่องว่างถูกต้อง
          let filename = "";
          const disposition = response.headers["content-disposition"];
          if (disposition && disposition.indexOf("attachment") !== -1) {
        // ดึงชื่อไฟล์จาก Content-Disposition โดยไม่ใช้ regex
        // (หลีกเลี่ยงปัญหา escape ของ \n ในไฟล์ .vue ซึ่งเคยทำให้ bundle พัง)
        const marker = "filename=";
        const at = disposition.indexOf(marker);
        if (at !== -1) {
          filename = disposition.substring(at + marker.length);
          const semi = filename.indexOf(";");
          if (semi !== -1) filename = filename.substring(0, semi);
          filename = filename.trim().replace(/^["']|["']$/g, "");
        }
          }

          const blob = new Blob([response.data], { type: contentType });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = decodeURIComponent(filename);
          link.click();
        })
        .catch(function() {
          Swal.fire(
            "เกิดข้อผิดพลาด",
            "สร้างไฟล์ Excel ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
            "error"
          );
        })
        .then(function() {
          KTApp.unblockPage();
        });
    },
    // FS-029 — สถานะอายุ Export Excel ของเอกสารใบนี้
    //
    //   แหล่งที่ถูกต้องคือ /Api/FormSummaryStock/Document ซึ่ง handler คำนวณให้แล้ว
    //   (GetFormSummaryStockDocumentQueryHandler — สูตรเดียวกับหน้ารายการ)
    //   แต่ response ของ endpoint นั้นยัง "ไม่" ส่ง 4 ฟิลด์นี้ออกมา เพราะการเพิ่มฟิลด์
    //   ต้องแก้ FormSummaryStockController ซึ่งอยู่นอกขอบเขตงานรอบนี้
    //   → ระหว่างนี้จึงอ่านแถวของใบเดียวกันจากผลค้นหา (endpoint ที่ส่งฟิลด์นี้อยู่แล้ว)
    //   เมื่อวันหนึ่ง /Document ส่งฟิลด์มาเอง computed excelExpiry จะเลือกใช้ค่านั้นก่อน
    //   และฟังก์ชันนี้จะไม่ยิง API ซ้ำอีก (เช็กที่บรรทัดแรก)
    //
    //   best-effort — ล้มเหลวก็ไม่รบกวนผู้ใช้ด้วย popup: ปุ่มจะทำงานตามปกติแล้วให้
    //   ExportFormSummaryStockExcelCommandHandler เป็นด่านปฏิเสธเหมือนเดิม
    loadExcelExpiry: function() {
      const self = this;

      if (!self.document) return;
      if (self.document.isExcelAvailable !== undefined) return;

      const documentNo = self.document.documentNo;
      if (!documentNo) return;

      axios
        .post(
          "/Api/FormSummaryStock/Search",
          {
            draw: 1,
            start: 0,
            length: 5,
            documentNo: documentNo,
            search: { value: "", regex: false },
          },
          { headers: { Authorization: "Bearer " + self.$root.user.access_token } }
        )
        .then(function(response) {
          const rows =
            response && response.data && response.data.data ? response.data.data : [];

          // ผลค้นหาเป็นแบบ "มีคำนี้อยู่ในเลขที่เอกสาร" จึงอาจได้ใบอื่นติดมาด้วย
          // ต้องจับคู่เลขที่เอกสารให้ตรงเป๊ะ ห้ามหยิบแถวแรกมาใช้ดื้อ ๆ
          let match = null;
          for (let i = 0; i < rows.length; i++) {
            if (rows[i] && rows[i].documentNo === documentNo) {
              match = rows[i];
              break;
            }
          }

          // ใบยังเปลี่ยนไม่ทัน (ผู้ใช้กดดูใบอื่นระหว่างรอ) = ทิ้งผลลัพธ์นี้
          if (self.document && self.document.documentNo === documentNo) {
            self.excelExpiryFallback = match;
          }
        })
        .catch(function() {
          self.excelExpiryFallback = null;
        });
    },

    // F04 (D10) — บรรทัดสาขาย่อย ใช้ endpoint เดียวกับฟอร์ม PDF
    //   โหลดไม่สำเร็จก็ปล่อยว่างไว้ ไม่ขึ้น error (เป็นข้อมูลประกอบ)
    loadBranchLine: function() {
      const self = this;

      if (!self.headerId) return;

      axios
        .post(
          "/Api/FormSummaryStock/CustomerBox",
          { headerId: self.headerId },
          { headers: { Authorization: "Bearer " + self.$root.user.access_token } }
        )
        .then(function(response) {
          const data = (response && response.data) || {};
          self.branchLine = data.success && data.hasBranchLine ? data.branchLine : "";
        })
        .catch(function() {
          self.branchLine = "";
        });
    },

    // FS-012 — อ่านว่าเอกสารฉบับนี้มีภาพลายเซ็นตรึงไว้หรือยัง เพื่อกำหนดสี/ข้อความบนปุ่ม
    //   ใช้ endpoint เดียวกับ popup (ห้ามแก้ backend ในรอบนี้) และเป็น best-effort
    //   ล้มเหลวก็แค่ปุ่มกลับไปเป็นโทนจาง ไม่ต้องรบกวนผู้ใช้ด้วย popup error
    loadSignatureState: function() {
      const self = this;

      self.signatureAttachedId = null;
      self.hasDefaultSignature = false;
      self.signatureImageUrl = "";
      // FS-018 — รีเซ็ตเป็น "ไม่มีสิทธิ์" ทุกครั้งที่โหลดเอกสารใหม่ (ซ่อนปุ่มไว้ก่อน)
      self.canManageSignature = false;
      if (!self.headerId) return;

      axios
        .post(
          "/Api/FormSummaryStock/Signature",
          { headerId: self.headerId },
          { headers: { Authorization: "Bearer " + self.$root.user.access_token } }
        )
        .then(function(response) {
          const data = (response && response.data) || {};

          // FS-018 — handler เซ็ต CanManage ไว้ตั้งแต่บรรทัดแรกของ Handle() จึงเชื่อถือได้
          //   แม้ success = false — อ่านก่อนด่าน success กันกรณี storage ล่มแล้วกลุ่ม A เสียปุ่มไปด้วย
          self.canManageSignature = !!data.canManage;

          if (!data.success) return;

          self.signatureAttachedId = data.documentAttachedId || null;
          // FS-017 — ไม่มีภาพเฉพาะเอกสาร = ใช้ค่าเริ่มต้นของระบบ ซึ่งก็ถือว่ามีลายเซ็น
          self.hasDefaultSignature = !data.isMissingDefault && !!data.defaultAttachedId;
          self.signatureImageUrl = data.documentImageUrl || data.defaultImageUrl || "";
        })
        .catch(function() {
          self.signatureAttachedId = null;
          self.hasDefaultSignature = false;
          self.signatureImageUrl = "";
          // FS-018 — เรียกไม่สำเร็จ = ไม่รู้สิทธิ์ ให้ถือว่าไม่มีสิทธิ์ (ซ่อนปุ่มไว้ก่อน)
          self.canManageSignature = false;
        });
    },

    // F08 — popup แนบไฟล์เอกสาร
    //   blocker เดิม (API Document ไม่ส่ง Header.Id) ถูกปิดไปแล้ว — response มี id แล้ว
    openAttachment: function() {
      if (!this.headerId) {
        Swal.fire("เกิดข้อผิดพลาด", "ไม่พบเลขที่อ้างอิงของเอกสารนี้", "error");
        return;
      }
      this.$refs.attachmentModal.open({
        id: this.headerId,
        documentNo: this.documentNo,
      });
    },

    // F15 — popup ลายเซ็นผู้จัดทำ (เอกสารบันทึกแล้ว จึงส่ง headerId จริง)
    openSignature: function() {
      if (!this.headerId) {
        Swal.fire("เกิดข้อผิดพลาด", "ไม่พบเลขที่อ้างอิงของเอกสารนี้", "error");
        return;
      }
      this.$refs.signatureModal.open({
        id: this.headerId,
        documentNo: this.documentNo,
      });
    },
  },
  components: {
    "form-summary-stock-attachment-modal": FormSummaryStockAttachmentModal,
    FormSummaryStockSignatureModal,
  },
  watch: {
    // กดดูเอกสารใบอื่นขณะอยู่หน้านี้ (component ถูก reuse) → โหลดใบใหม่
    "$route.params.documentNo": function() {
      this.loadDocument();
    },
  },
  async mounted() {
    await this.$nextTick();
    this.loadDocument();
  },
};
</script>

<style lang="scss" scoped>

/* QA เรื่องที่ 17 — หน้าแจ้งเมื่อไม่พบเอกสาร */
.fss-detail__notfound {
  text-align: center;
  padding: 48px 20px;
}
.fss-detail__notfound-icon {
  font-size: 48px;
  color: #d1d5db;
  margin-bottom: 16px;
  display: block;
}
.fss-detail__notfound-title {
  font-size: 20px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 8px;
}
.fss-detail__notfound-text {
  color: #6b7280;
  font-size: 14px;
}
.fss-detail__notfound-doc {
  margin-top: 6px;
  color: #9ca3af;
  font-size: 13px;
}

/* QA เรื่องที่ 8 — ปุ่ม Export Excel กับคำเตือนวันหมดอายุเป็นก้อนเดียวกัน
   ใช้ inline-flex แนวตั้ง เพื่อให้คำเตือนตกมาอยู่ใต้ปุ่มพอดี
   ไม่ไปดันปุ่มอื่นในแถว และยังอยู่ในแถว text-right เดิมได้ */
.fss-detail__excel-group {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  vertical-align: top;
}
.fss-detail__expire-under {
  margin-top: 4px;
  white-space: nowrap;
}
/* FS-017 — thumbnail ลายเซ็นข้างปุ่ม (ตาม design) ภาพมาจาก signed URL ของ endpoint Signature
   ห้ามชี้ไปที่ storage.googleapis.com ตรง ๆ — bucket ปิด เบราว์เซอร์จะได้ 403 (เหตุผลเดียวกับ FS-015) */
.fss-signature-thumb {
  height: 28px;
  max-width: 96px;
  object-fit: contain;
  background: #fff;
  border: 1px solid #ebedf2;
  border-radius: 3px;
  padding: 1px 3px;
  vertical-align: middle;
}

/* FS-098 / FS-116 — ค่าชุดเดียวกับขั้นที่ 2 (FormSummaryStockPreview.vue .fss-branch-line) */
.fss-branch-line {
  margin-top: 0.25rem;
  color: #74788d;
  font-size: 0.85rem;
  font-weight: normal;
}

/* FS-036 ข้อ 5 — บรรทัดบอกจำนวนรายการระดับบาร์โค้ดในไฟล์ Excel
   สีเทาเหมือนข้อความประกอบอื่น ๆ ของหน้านี้ (fss-branch-line) */
.fss-excel-note {
  margin-top: 0.5rem;
  color: #74788d;
  font-size: 0.85rem;
}

/* FS-029 — บรรทัดแจ้งวันหมดอายุ Export Excel
   ค่าสี/ขนาดชุดเดียวกับหน้ารายการ (FormSummaryStockList.vue) ห้ามเพี้ยนจากกัน
   design ให้คำเตือนเป็นสีแดง ส่วนวันที่เป็นสีปกติ — ถ้าแดงทั้งบรรทัดจะอ่านวันที่ยาก
   เพราะสายตาไปจับคำเตือนก่อน */
/* FS-048 — ย้ายมาอยู่ "หน้าปุ่มพิมพ์ PDF" จึงต้องเป็น inline ไม่ใช่บล็อกเต็มบรรทัด */
.fss-expire-line {
  display: inline-block;
  margin-right: 0.75rem;
  font-size: 0.8rem;
  white-space: nowrap;
  vertical-align: middle;
}

.fss-expire-warn {
  color: #fd397a;
  font-weight: 500;
}

.fss-expire-date {
  color: #48465b;
}

.fss-readonly {
  padding: 0.5rem 0.75rem;
  background-color: #f7f8fa;
  border-radius: 4px;
  min-height: 2.5rem;
}

/* FS-118 — ค่าเดียวกับขั้นที่ 2 (FormSummaryStockPreview.vue .fss-filter-text / .fss-filter-table) */
.fss-filter-text {
  padding: 0.5rem 0.75rem;
  background-color: #f7f8fa;
  border-radius: 4px;
}

.fss-filter-table {
  margin-bottom: 0;

  &__label {
    width: 12rem;
    font-weight: 600;
    color: #6c7293;
  }
}

.fss-total-row {
  background-color: #f7f8fa;
}

/* FS-116 — แถวรายละเอียดใต้แถวตัวแทน : ค่าเดียวกับ FormSummaryStockPreview.vue (.fss-detail)
   ใช้ชื่อ fss-detail-box เพราะหน้านี้มีคลาส fss-detail__* ของส่วนอื่นอยู่แล้ว */
.fss-detail-row > td {
  background-color: #fafbfc;
}

.fss-detail-box {
  padding: 0.5rem 0.25rem;

  &__title {
    margin-bottom: 0.5rem;
    font-weight: 600;
    color: #6c7293;
  }
}

.fss-summary {
  margin-top: 1rem;
  padding: 1rem 1.25rem;
  background-color: #f7f8fa;
  border-radius: 4px;

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 0.25rem 0;
  }

  &__label {
    color: #6c7293;
    margin-right: 1rem;
  }

  &__value {
    font-weight: 600;
    text-align: right;
  }
}
</style>



// WEBPACK FOOTER //
// App/Components/PrintSummary/FormSummaryStockDetail.vue